// selector combining state trees order, customer, stand
import { find, get, sortBy, uniqBy, keys, compact, map, filter, isEmpty, reduce } from 'lodash'
import { createSelector } from 'reselect'

import { getAttendantByStandUuidAndEventId } from '../AppDeviceConfiguration/selector'
import { makeGetAttendant } from '../Staff/selector'
import { makeGetPreOrdersByEvent } from '../Order/selector'
import { getToggles, makeGetCustomer } from '../Customer/selector'
import { makeGetRevenueCenter, getRevenueCenters } from '../RevenueCenter/selector'
import { makeGetCustomerPayments } from '../CustomerPayment/selector'
import { getAllCustomerOrderingPreferencesById, makeGetCustomerOrderingPreferencesByType, getDefaultPreferences } from '../../store/CustomerOrderingPreference/selector'
import makeCustomerPreferences from '../../utils/customerPreferences'

// TODO: have this function run in a saga triggered by when customers or attendants get added
// and keep suites up to date byId so that they can be more available to be accessed
const getAllDisplaySuites = (state, eventId) => { // includes empty suites
  const getCustomerStandPreferences = makeGetCustomerOrderingPreferencesByType()
  const getPreOrdersByEvent = makeGetPreOrdersByEvent()

  const attendantByStandUuid = getAttendantByStandUuidAndEventId(state, eventId)
  const preOrdersByEvent = getPreOrdersByEvent(state, eventId)
  const rvcs = getRevenueCenters(state)
  let customerStandPreferences = getCustomerStandPreferences(state, 'Customer::StandPreference')

  customerStandPreferences = filter(customerStandPreferences, ['salesEventId', eventId])

  const suites = rvcs.map(rvc => {
    const suiteGroup = {
      rvcName: rvc.name,
      rvcUuid: rvc.uuid,
      customers: [],
      attendants: []
    }

    get(attendantByStandUuid, `${rvc.uuid}`, []).forEach(attendantUuid => {
      const getAttendant = makeGetAttendant()
      const attendant = getAttendant(state, attendantUuid)

      suiteGroup.attendants.push({ attendantUuid: attendant?.uuid, attendantName: attendant?.name, employeeId: attendant?.employeeId, roleId: attendant?.id })
    })

    preOrdersByEvent.forEach(order => {
      if (order.standUuid === rvc.uuid) {
        const getCustomer = makeGetCustomer()
        const customer = getCustomer(state, order.customerId)
        const customerName = get(customer, 'name', 'No Customer')

        suiteGroup.customers.push({customerId: customer.uuid, customerName: customerName})
      }
    })

    customerStandPreferences.forEach(pref => {
      if (pref.standUuid === rvc.uuid) {
        const getCustomer = makeGetCustomer()
        const customer = getCustomer(state, pref.customerId)
        const customerName = get(customer, 'name', 'No Customer')

        suiteGroup.customers.push({customerId: customer.uuid, customerName: customerName, preferenceUuid: pref.uuid})
      }
    })
    // uniqBy will remove the duplicate added latest in customers array
    suiteGroup.customers = uniqBy(suiteGroup.customers, 'customerId')

    return suiteGroup
  })

  // return suites (stands) in order returned from backend
  return suites
}

export const makeGetAllDisplaySuites = () => createSelector(
  [getAllDisplaySuites], (suite) => suite
)

const getDisplaySuites = (state, eventId) => {
  const getAllDisplaySuites = makeGetAllDisplaySuites()
  const allDisplaySuites = getAllDisplaySuites(state, eventId)
  const displayAssignedCustomers = get(getToggles(state), 'displayAssignedCustomers')
  const suiteWithCustomersOrAttendants = displayAssignedCustomers ? allDisplaySuites.filter(suite => !isEmpty(suite.customers)) : allDisplaySuites.filter(suite => !isEmpty(suite.attendants))

  return suiteWithCustomersOrAttendants
}

export const makeGetDisplaySuites = () => createSelector(
  [getDisplaySuites, makeGetAllDisplaySuites, makeGetPreOrdersByEvent, makeGetCustomer, makeGetRevenueCenter],
  (suites) => suites
)

const getDisplaySuite = (state, eventId, standId, customerId = null) => {
  const getAllDisplaySuites = makeGetAllDisplaySuites()
  const displaySuites = getAllDisplaySuites(state, eventId)
  const displaySuite = displaySuites.find(suite => suite.rvcUuid === standId)
  if (customerId) {
    const customer = find(displaySuite.customers, (c) => c.customerId === customerId)

    return {
      rvcName: displaySuite.rvcName,
      rvcUuid: displaySuite.rvcUuid,
      customerId: customer.customerId,
      customerName: customer.customerName
    }
  }
  return displaySuite
}

export const makeGetDisplaySuite = () => createSelector(
  [getDisplaySuite, makeGetAllDisplaySuites], (suite) => suite
)

export const getFormRegisteredFields = (state) => keys(get(state, 'form.ordering-preference.registeredFields'))

const getSuitesByCustomer = (state, customerId) => {
  const getCustomerPayments = makeGetCustomerPayments()
  const defaultPreferences = getDefaultPreferences(state)
  const revenueCenters = getRevenueCenters(state)

  const suiteIds = keys(defaultPreferences[customerId])
  const customerSuites = sortBy(compact(map(suiteIds, (suiteId) => {
        return find(revenueCenters, ['uuid', suiteId])
  })), (suite) => suite.name)

  const customerPayments = getCustomerPayments(state, customerId)
  const customerOrderingPreferences = getAllCustomerOrderingPreferencesById(state)
  const nonSuiteDefaultPreferences = map(defaultPreferences[customerId]?.noStand, (prefId) => customerOrderingPreferences[prefId])
  const suitesByCustomer = reduce(defaultPreferences[customerId], (suiteAndPreferenceInfo, suitePrefIds, suiteId) => {
    const suitePreferences = map(suitePrefIds, (preferenceId) => customerOrderingPreferences[preferenceId])
    const suite = find(customerSuites, (suite) => suite?.uuid === suiteId) || {}
    if (isEmpty(suite)) return suiteAndPreferenceInfo

    return [ ...suiteAndPreferenceInfo, {
      suite,
      ...makeCustomerPreferences([ ...suitePreferences, ...nonSuiteDefaultPreferences ], customerPayments, null)
    }]
  }, [])

  return suitesByCustomer
}

export const makeGetSuitesByCustomer = () => createSelector(
  [getSuitesByCustomer],
  (suites) => suites
)

export const getSelectedSuites = (state) => {
  return get(state, 'suite.selectedSuites', {})
}

export const getPendingCustomerOrderingPreferenceIds = state => map(
  state?.customerOrderingPreference?.pendingPreferences || [], pendingPreference => pendingPreference.uuid
)
